import type { Aabb, InteriorTemplate } from "@townsy/contracts";
import { YAW_NORTH, YAW_SOUTH, aabb, aabbContains } from "../geometry";
import { fixture } from "./common";

/**
 * Townsy Downs (ADR-0016): the race track room. Local coordinates: x grows east, z grows north; the entrance and exit
 * door are on the south wall (z = -40) at x = 0. From south to north:
 *
 *   z -40..-33  the betting hall by the entrance: two betting kiosks (three windows each) either side of the door;
 *   z -33..-29  the grandstand, two sections (x -40..-4 and 4..40) of four tiered rows facing north, with a centre
 *               aisle (x -4..4) from the hall to the rail;
 *   z -29..-18.6 the apron, where visitors stand at the rail;
 *   z -18.6..-18.1 the apron fence (the home straight's outer rail), wall to wall: nobody walks onto the track;
 *   north of it the racecourse: a dirt oval run anticlockwise seen from above (west to east past the grandstand),
 *               the grass infield with the tote board facing the stands, the finish line at x = 0.
 *
 * Everything the game and the renderer both need (the course path and its length, lanes, the finish, seats, betting
 * windows, the tote board) is exported from here so they cannot disagree.
 */
export const RACE_ROOM: Aabb = aabb(-75, -40, 75, 40);

/** The course: two straights (x = -straightHalf..straightHalf) joined by half circles around (±straightHalf, centreZ). */
export const RACE_COURSE = {
  straightHalf: 44,
  /** Radius of the running surface's centre line at the turns. */
  radius: 22,
  /** z of the two turn centres. */
  centreZ: 9,
  /** Width of the running surface (inner rail to outer rail). */
  width: 10,
  lanes: 8,
  laneWidth: 1.15,
} as const;

/** Length of one lap along the centre line (metres). A race is one lap, from the finish line back to it. */
export const RACE_COURSE_LENGTH = 4 * RACE_COURSE.straightHalf + 2 * Math.PI * RACE_COURSE.radius;
/** The race distance (one lap: the start is the finish line, in front of the grandstand). */
export const RACE_DISTANCE = RACE_COURSE_LENGTH;
/** z of the home straight's centre line (in front of the grandstand). */
export const RACE_HOME_STRAIGHT_Z = RACE_COURSE.centreZ - RACE_COURSE.radius;
/** The finish line crosses the home straight at x = 0 (also where the horses line up to start). */
export const RACE_FINISH_X = 0;
/** Inner and outer rail radii at the turns (inner rail z = -8 and outer rail z = -18 on the home straight). */
export const RACE_RAILS = { inner: RACE_COURSE.radius - RACE_COURSE.width / 2, outer: RACE_COURSE.radius + RACE_COURSE.width / 2 } as const;
/** The apron fence (the home straight's outer rail, wall to wall): its collider. */
export const RACE_FENCE: Aabb = aabb(RACE_ROOM.minX, -18.6, RACE_ROOM.maxX, -18.1);
/** The finish post on the inside rail (with the judge's mirror disc), and its twin standing on the apron fence. */
export const RACE_FINISH_POSTS = [
  { x: RACE_FINISH_X, z: RACE_HOME_STRAIGHT_Z + RACE_COURSE.width / 2 + 0.45 },
  { x: RACE_FINISH_X, z: (RACE_FENCE.minZ + RACE_FENCE.maxZ) / 2 },
] as const;

export interface RaceCoursePoint {
  x: number;
  z: number;
  /** Direction of running (yaw: 0 = north, PI/2 = east). */
  heading: number;
}

/**
 * The point `s` metres along the course from the finish line (anticlockwise; any s, wrapped to one lap) and `offset`
 * metres sideways from the centre line (positive = towards the outer rail).
 */
export function raceCoursePoint(s: number, offset = 0): RaceCoursePoint {
  const { straightHalf: S, radius: R, centreZ: zc } = RACE_COURSE;
  const L = RACE_COURSE_LENGTH;
  let d = s % L;
  if (d < 0) d += L;
  const turn = Math.PI * R;
  const r = R + offset;
  // Home straight, finish line to the east turn (running east).
  if (d < S) return { x: d, z: zc - r, heading: Math.PI / 2 };
  d -= S;
  // East turn (anticlockwise from its south point to its north point).
  if (d < turn) {
    const a = -Math.PI / 2 + d / R;
    return { x: S + r * Math.cos(a), z: zc + r * Math.sin(a), heading: Math.atan2(-Math.sin(a), Math.cos(a)) };
  }
  d -= turn;
  // Back straight (running west).
  if (d < 2 * S) return { x: S - d, z: zc + r, heading: -Math.PI / 2 };
  d -= 2 * S;
  // West turn (from its north point to its south point).
  if (d < turn) {
    const a = Math.PI / 2 + d / R;
    return { x: -S + r * Math.cos(a), z: zc + r * Math.sin(a), heading: Math.atan2(-Math.sin(a), Math.cos(a)) };
  }
  d -= turn;
  // Home straight, west end to the finish line.
  return { x: -S + d, z: zc - r, heading: Math.PI / 2 };
}

/** Sideways offset of a lane's centre (lane 1 = the inside rail, lane 8 = outside). */
export function raceLaneOffset(lane: number): number {
  const { width, laneWidth, lanes } = RACE_COURSE;
  const margin = (width - lanes * laneWidth) / 2;
  return -width / 2 + margin + laneWidth * (lane - 0.5);
}

// ---------------------------------------------------------------------------------------------
// Grandstand and seats
// ---------------------------------------------------------------------------------------------

/**
 * The grandstand: two sections of four rows rising towards the back (south). A row is 1 m deep; its floor (tread) is
 * 1.0 m above the apron for the front row and 0.5 m higher for each row behind. Seats face north, at the finish line.
 */
export const RACE_GRANDSTAND = {
  frontZ: -29,
  rows: 4,
  rowDepth: 1,
  firstRowY: 1.0,
  rowRise: 0.5,
  seatPitch: 1.0,
  sections: [
    { id: "west", minX: -40, maxX: -4 },
    { id: "east", minX: 4, maxX: 40 },
  ],
} as const;
/** South edge of the grandstand (the back of the top row). */
export const RACE_GRANDSTAND_BACK_Z = RACE_GRANDSTAND.frontZ - RACE_GRANDSTAND.rows * RACE_GRANDSTAND.rowDepth;

export interface RaceSeat {
  index: number;
  section: "west" | "east";
  /** 0 = front row. */
  row: number;
  /** Where a seated visitor's avatar stands (its hips above this point), on the row's tread. */
  x: number;
  /** Height of the row's tread (the seated avatar's feet). */
  y: number;
  z: number;
  facing: number;
}

function buildSeats(): RaceSeat[] {
  const g = RACE_GRANDSTAND;
  const seats: RaceSeat[] = [];
  for (const sec of g.sections) {
    const count = Math.floor((sec.maxX - sec.minX) / g.seatPitch);
    const x0 = sec.minX + (sec.maxX - sec.minX - (count - 1) * g.seatPitch) / 2;
    for (let row = 0; row < g.rows; row++) {
      for (let k = 0; k < count; k++) {
        seats.push({
          index: seats.length,
          section: sec.id,
          row,
          x: Math.round((x0 + k * g.seatPitch) * 100) / 100,
          y: g.firstRowY + row * g.rowRise,
          // Hips over the back half of the tread; the feet rest near its front edge.
          z: g.frontZ - row * g.rowDepth - 0.7,
          facing: YAW_NORTH,
        });
      }
    }
  }
  return seats;
}

/** Every grandstand seat (index = position in this list). */
export const RACE_SEATS: readonly RaceSeat[] = buildSeats();
/** A visitor may sit in a free seat at most this far away (metres); the server picks the nearest. */
export const RACE_SIT_RANGE = 6;
/** A seated visitor who moved this far from the seat has walked away (the seat is freed). */
export const RACE_SEAT_LEAVE_DISTANCE = 0.5;

/** The grandstand footprint of a section (its collider). */
export function raceGrandstandBox(section: (typeof RACE_GRANDSTAND.sections)[number]): Aabb {
  return aabb(section.minX, RACE_GRANDSTAND_BACK_Z, section.maxX, RACE_GRANDSTAND.frontZ);
}

/** Distance from a point to the nearest grandstand section (0 inside). */
export function raceDistanceToGrandstand(x: number, z: number): number {
  let best = Number.POSITIVE_INFINITY;
  for (const sec of RACE_GRANDSTAND.sections) {
    const b = raceGrandstandBox(sec);
    const dx = Math.max(b.minX - x, 0, x - b.maxX);
    const dz = Math.max(b.minZ - z, 0, z - b.maxZ);
    best = Math.min(best, Math.hypot(dx, dz));
  }
  return best;
}

/** The free seat nearest to (x, z) within RACE_SIT_RANGE, or null. Ties go to the lower seat index. */
export function raceNearestFreeSeat(x: number, z: number, taken: ReadonlySet<number>, range = RACE_SIT_RANGE): RaceSeat | null {
  let best: RaceSeat | null = null;
  let bestD = range;
  for (const seat of RACE_SEATS) {
    if (taken.has(seat.index)) continue;
    const d = Math.hypot(seat.x - x, seat.z - z);
    if (d <= bestD && (best === null || d < bestD)) {
      best = seat;
      bestD = d;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------------------------
// Betting windows, the betting hall and the tote board
// ---------------------------------------------------------------------------------------------

/** The two betting kiosks against the south wall, either side of the door (their colliders). */
export const RACE_KIOSKS: readonly Aabb[] = [aabb(-11, -40, -3.5, -38), aabb(3.5, -40, 11, -38)];
/** The six betting windows (on the kiosks' north faces, facing the hall). */
export const RACE_BETTING_WINDOWS: readonly { x: number; z: number; facing: number }[] = RACE_KIOSKS.flatMap((k) =>
  [0.2, 0.5, 0.8].map((t) => ({ x: Math.round((k.minX + (k.maxX - k.minX) * t) * 100) / 100, z: k.maxZ, facing: YAW_NORTH })),
);
/**
 * The betting hall: the floor in front of the betting windows, by the entrance (marked on the floor). Bets are taken
 * from visitors standing here or sitting in the grandstand.
 */
export const RACE_BETTING_HALL: Aabb = aabb(-16, RACE_ROOM.minZ, 16, RACE_GRANDSTAND_BACK_Z);
export const RACE_BETTING_ZONE_TEXT = "Bets are taken at the betting windows by the entrance (the green floor) or from a seat in the grandstand.";

export function raceInBettingHall(x: number, z: number): boolean {
  return aabbContains(RACE_BETTING_HALL, x, z);
}

/** The tote board in the infield, facing the grandstand across the finish line (odds, countdown, results). */
export const RACE_TOTE_BOARD = { x: 0, z: 3, bottomY: 3.2, width: 19, height: 8, facing: YAW_SOUTH } as const;

/**
 * Fixture colliders: the apron fence, the two grandstand sections and the betting kiosks. The `venue_` prefix tells the
 * generic interior builder not to draw them (the race track's own 3D draws the fence, stands and kiosks).
 */
export function raceTrackColliders() {
  return [
    fixture("venue_race_fence", RACE_FENCE),
    ...RACE_GRANDSTAND.sections.map((s) => fixture(`venue_race_stand_${s.id}`, raceGrandstandBox(s))),
    ...RACE_KIOSKS.map((k, i) => fixture(`venue_race_kiosk_${i === 0 ? "west" : "east"}`, k)),
  ];
}

/**
 * Townsy Downs room (ADR-0016): one shared room for everyone at the track. The venue name sign sits on top of the tote
 * board, facing the grandstand.
 */
export function raceTrackTemplate(): InteriorTemplate {
  const t = RACE_TOTE_BOARD;
  return {
    id: "race_track",
    venue: "race_track",
    bounds: RACE_ROOM,
    spawn: { position: { x: 0, z: RACE_ROOM.minZ + 2.4 }, facing: YAW_NORTH },
    exit: { position: { x: 0, z: RACE_ROOM.minZ + 1 }, facing: YAW_SOUTH },
    exitDoor: { position: { x: 0, z: RACE_ROOM.minZ }, direction: { x: 0, z: -1 }, halfWidth: 1.6 },
    // On top of the tote board (its frame and green cap end 0.85 m above the screen area).
    signAnchor: { kind: "storefront", position: { x: t.x, y: t.bottomY + t.height + 2.55, z: t.z + 0.15 }, facing: YAW_SOUTH, width: 12, height: 3 },
    colliders: raceTrackColliders(),
  };
}
